
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type CurrentUser } from '../firebase.ts';
import { TitleIcon, SuccessIcon } from '../constants.tsx';

interface SupportProps {
    currentUser: CurrentUser | null;
}

const Support: React.FC<SupportProps> = ({ currentUser }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const isLoggedIn = !!currentUser;

    useEffect(() => {
        if (isLoggedIn) {
            setName(currentUser.profile.displayName || '');
            setEmail(currentUser.auth.email || '');
            setPhone('');
            setMessage('');
            setStatus('idle');
            setErrorMessage('');
        } else {
            setName('');
            setEmail('');
            setPhone('');
            setMessage('');
            setStatus('idle');
        }
    }, [isLoggedIn, currentUser]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || !email.trim() || !message.trim()) {
            setStatus('error');
            setErrorMessage(t('formErrorRequired'));
            return;
        }
        setStatus('submitting');
        setErrorMessage('');

        try {
            console.log('Submitting support ticket:', { name, email, phone, message });

            // Send to Cloudflare Worker to email admin
            const workerResponse = await fetch('https://tad-contact-worker.itsmybadsoul.workers.dev/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email,
                    phone,
                    message,
                    userId: currentUser?.auth?.uid || 'anonymous'
                }),
            });

            if (!workerResponse.ok) {
                console.error('Worker failed to send email:', await workerResponse.text());
                setStatus('error');
                setErrorMessage('Failed to send message. Please try again later.');
                return;
            }

            console.log('Support ticket sent successfully to Cloudflare Worker.');
            setStatus('success');
            // Don't clear name/email for logged in user, just message and phone
            setPhone('');
            setMessage('');
        } catch (error) {
            console.error('Error submitting support ticket:', error);
            setStatus('error');
            setErrorMessage(t('formErrorGeneric'));
        }
    };

    return (
        <section id="support" className="scroll-mt-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 xl:px-0">
                <div className="relative z-10 overflow-hidden rounded-[30px] bg-dark pt-25 px-4 sm:px-20 lg:px-28">
                    <div className="absolute -z-10 pointer-events-none inset-0">
                        <div className="absolute left-1/2 -translate-x-1/2 top-0 w-full max-w-4xl h-[400px] bg-gradient-to-t from-purple-900/5 to-fuchsia-900/20 blur-[150px]"></div>
                    </div>

                    <div className="mb-16 text-center relative z-10">
                        <span className="hero-subtitle-gradient relative mb-4 font-medium text-sm inline-flex items-center gap-2 py-2 px-4.5 rounded-full">
                            <TitleIcon />
                            <span className="hero-subtitle-text"> {t('supportSectionSubtitle')} </span>
                        </span>
                        <h2 className="text-white mb-4.5 text-2xl font-extrabold sm:text-4xl">
                            {t('supportSectionTitle')}
                        </h2>
                        <p className="max-w-3xl mx-auto font-medium">
                            {t('supportSectionDescription')}
                        </p>
                    </div>

                    <div className="support-form-gradient mb-20">
                        <div className="support-form-inner-box p-6 sm:p-8 xl:p-14 min-h-[480px] flex items-center justify-center">
                            {status === 'success' ? (
                                <div className="text-center">
                                    <div className="flex justify-center mb-6">
                                        <SuccessIcon />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-3">{t('formSuccessTitle')}</h3>
                                    <p className="text-white/80">{t('formSuccessMessage')}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="relative z-10 w-full">
                                    <div className="-mx-4 xl:-mx-5 flex flex-wrap transition-opacity">
                                        <div className="w-full px-4 xl:px-5 md:w-1/2">
                                            <div className="mb-8">
                                                <label htmlFor="name" className="text-white mb-2.5 block font-medium text-start">{t('formNameLabel')}</label>
                                                <input id="name" type="text" name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('formNamePlaceholder')} className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-brand w-full py-3 px-6 outline-none transition-colors disabled:bg-white/[0.02]" required />
                                            </div>
                                        </div>
                                        <div className="w-full px-4 xl:px-5 md:w-1/2">
                                            <div className="mb-8">
                                                <label htmlFor="email" className="text-white mb-2.5 block font-medium text-start">{t('formEmailLabel')}</label>
                                                <input id="email" type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('formEmailPlaceholder')} className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-brand w-full py-3 px-6 outline-none transition-colors disabled:bg-white/[0.02]" required />
                                            </div>
                                        </div>
                                        <div className="w-full px-4 xl:px-5">
                                            <div className="mb-8">
                                                <label htmlFor="phone" className="text-white mb-2.5 block font-medium text-start">{t('formPhoneLabel')}</label>
                                                <input id="phone" type="tel" name="phone" value={phone} onChange={(e: any) => setPhone(e.target.value)} placeholder={t('formPhonePlaceholder')} className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-brand w-full py-3 px-6 outline-none transition-colors disabled:bg-white/[0.02]" required />
                                            </div>
                                        </div>
                                        <div className="w-full px-4 xl:px-5">
                                            <div className="mb-8">
                                                <label htmlFor="message" className="text-white mb-2.5 block font-medium text-start">{t('formMessageLabel')}</label>
                                                <textarea id="message" name="message" value={message} onChange={(e: any) => setMessage(e.target.value)} placeholder={t('formMessagePlaceholder')} rows={6} className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-brand w-full py-5 px-6 outline-none transition-colors disabled:bg-white/[0.02]" required></textarea>
                                            </div>
                                        </div>
                                        <div className="w-full px-4 xl:px-5">
                                            <div className="text-center">
                                                <button type="submit" disabled={status === 'submitting'} className="hero-button-gradient inline-flex rounded-lg py-3 px-7 text-white font-medium ease-in duration-300 hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed">
                                                    {status === 'submitting' ? t('formSubmitting') : t('formSendButton')}
                                                </button>
                                                {status === 'error' && <p className="text-red-400 mt-4">{errorMessage}</p>}
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Support;